import React, { useState, useEffect } from 'react';
import { Copy, Check, RefreshCw, ExternalLink, TrendingUp, TrendingDown, Wallet, QrCode } from 'lucide-react';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { siteConfigApi, reconciliationApi } from '../utils/api';
import DonationExpansionModal from '../components/DonationExpansionModal';
import HonorList from '../components/HonorList';
import { useLanguageStore } from '../stores/languageStore';
import { t } from '../stores/languageStore';

interface Transaction {
  txhash: string;
  timestamp: number;
  from: string;
  to: string;
  value: string;
  tokenSymbol: string;
  type: 'in' | 'out';
}

interface WalletData {
  balance: string;
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
}

const Donation: React.FC = () => {
  const { language } = useLanguageStore();
  const [trc20Data, setTrc20Data] = useState<WalletData>({
    balance: '0',
    transactions: [],
    loading: true,
    error: null
  });

  const [erc20Data, setErc20Data] = useState<WalletData>({
    balance: '0',
    transactions: [],
    loading: true,
    error: null
  });

  const [copiedTrc20, setCopiedTrc20] = useState(false);
  const [copiedErc20, setCopiedErc20] = useState(false);
  const [showQrTrc20, setShowQrTrc20] = useState(false);
  const [showQrErc20, setShowQrErc20] = useState(false);
  const [configs, setConfigs] = useState<Record<string, string>>({
    donation_trc20_address: 'TGQZqNgqyKHH2kC5kFJMNsHw5zyoyVL9kR',
    donation_erc20_address: '0x906fe7f34c36900F7636cB731a44135762A752cd',
    donation_title: '捐助我们',
    donation_description: '感谢您对桃花坛的支持！您的慷慨捐助将帮助我们更好地维护和运营社区。我们支持 TRC20（波场）和 ERC20（以太坊）两种 USDT 代币捐助。',
    donation_thank_you: '捐助仅代表您对社区的支持，不提供任何特权，不影响社区免费生态。',
    donation_trc20_max_orders: '20',
    donation_erc20_max_orders: '20',
    donation_refresh_interval: '60',
    music_song_id: '1853863567'
  });
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  
  const [showDonationModal, setShowDonationModal] = useState(false);

  const loadConfigs = async () => {
    try {
      const response = await siteConfigApi.getPublicConfigs();
      if (response.success && response.data) {
        console.log('Loaded configs:', response.data);
        setConfigs(prev => ({
          ...prev,
          ...response.data
        }));
        setIsConfigLoaded(true);
      }
    } catch (error) {
      console.error('加载配置失败:', error);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  useEffect(() => {
    if (!configs.donation_refresh_interval || !isConfigLoaded) return;

    const refreshInterval = parseInt(configs.donation_refresh_interval) * 1000;
    const erc20RefreshInterval = Math.max(refreshInterval * 3, 30000); // ERC20 刷新间隔更长
    console.log('Setting up auto-refresh with interval:', refreshInterval, 'ms (ERC20 every', erc20RefreshInterval, 'ms)');

    fetchTrc20Data();
    fetchErc20Data();

    const interval = setInterval(() => {
      fetchTrc20Data();
    }, refreshInterval);

    const erc20Interval = setInterval(() => {
      fetchErc20Data();
    }, erc20RefreshInterval);

    return () => {
      clearInterval(interval);
      clearInterval(erc20Interval);
    };
  }, [isConfigLoaded, configs.donation_refresh_interval, configs.donation_trc20_address, configs.donation_erc20_address]);

  const [selectedNetworkType, setSelectedNetworkType] = useState<'trc20' | 'erc20'>('trc20');

  const openDonationModal = (network: 'trc20' | 'erc20' = 'trc20') => {
    setSelectedNetworkType(network);
    setShowDonationModal(true);
  };

  const closeDonationModal = () => {
    setShowDonationModal(false);
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatAmount = (value: string, decimals: number = 6) => {
    const num = parseFloat(value);
    if (num === 0) return '0';
    if (num < 0.000001) return '< 0.000001';
    return num.toFixed(decimals);
  };

  const copyToClipboard = async (text: string, type: 'trc20' | 'erc20') => {
    try {
      // 优先使用现代Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        if (type === 'trc20') {
          setCopiedTrc20(true);
          setTimeout(() => setCopiedTrc20(false), 2000);
        } else {
          setCopiedErc20(true);
          setTimeout(() => setCopiedErc20(false), 2000);
        }
        toast.success(language === 'en' ? 'Address copied' : '地址已复制');
      } else {
        // 降级方案：使用传统的execCommand方法
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
            if (type === 'trc20') {
              setCopiedTrc20(true);
              setTimeout(() => setCopiedTrc20(false), 2000);
            } else {
              setCopiedErc20(true);
              setTimeout(() => setCopiedErc20(false), 2000);
            }
            toast.success(language === 'en' ? 'Address copied' : '地址已复制');
          } else {
            throw new Error('复制命令执行失败');
          }
        } catch (copyError) {
          document.body.removeChild(textArea);
          throw copyError;
        }
      }
    } catch (error) {
      console.error('复制失败:', error);
      toast.error(language === 'en' ? 'Copy failed, please copy manually' : '复制失败，请手动复制地址');
    }
  };

  const fetchTrc20Data = async () => {
    try {
      setTrc20Data(prev => ({ ...prev, loading: true, error: null }));

      const address = configs.donation_trc20_address;

      let allTransactions: any[] = [];
      let hasMore = true;
      let limit = 100;
      let fingerprint = '';

      while (hasMore) {
        let url = `https://api.trongrid.io/v1/accounts/${address}/transactions/trc20?limit=${limit}&contract_address=TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t`;
        if (fingerprint) {
          url += `&fingerprint=${fingerprint}`;
        }

        const txRes = await fetch(url);
        if (!txRes.ok) {
          throw new Error('API请求失败');
        }

        const txData = await txRes.json();

        if (txData.data && txData.data.length > 0) {
          allTransactions = allTransactions.concat(txData.data);
          if (txData.meta && txData.meta.fingerprint) {
            fingerprint = txData.meta.fingerprint;
          } else {
            hasMore = false;
          }
          if (txData.data.length < limit) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      let balance = '0';
      const transactions: Transaction[] = [];
      if (allTransactions.length > 0) {
        allTransactions.forEach((tx: any) => {
          if (tx.token_info && tx.token_info.symbol === 'USDT') {
            const isIncoming = tx.to === address;
            transactions.push({
              txhash: tx.transaction_id,
              timestamp: Math.floor(tx.block_timestamp / 1000),
              from: tx.from,
              to: tx.to,
              value: (parseInt(tx.value) / Math.pow(10, tx.token_info.decimals)).toString(),
              tokenSymbol: tx.token_info.symbol,
              type: isIncoming ? 'in' : 'out'
            });
          }
        });
      }

      let totalIn = 0;
      let totalOut = 0;

      transactions.forEach(tx => {
        const amount = parseFloat(tx.value);
        if (tx.type === 'in') {
          totalIn += amount;
        } else {
          totalOut += amount;
        }
      });

      const calculatedBalance = totalIn - totalOut;
      balance = calculatedBalance.toFixed(2);

      const maxOrders = parseInt(configs.donation_trc20_max_orders || '20');
      const recentTransactions = transactions
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, maxOrders);

      setTrc20Data({
        balance,
        transactions: recentTransactions,
        loading: false,
        error: null
      });
    } catch (error: any) {
      setTrc20Data(prev => ({
        ...prev,
        loading: false,
        error: error.message || '获取数据失败'
      }));
    }
  };

  const fetchErc20Data = async () => {
    try {
      console.log('开始获取 ERC20 数据（通过后端代理）...');
      setErc20Data(prev => ({ ...prev, loading: true, error: null }));

      const address = configs.donation_erc20_address;
      console.log('ERC20 address:', address);

      const response = await reconciliationApi.getErc20Data(address);

      if (response.success && response.data) {
        console.log('ERC20 数据获取成功:', response.data);
        setErc20Data({
          balance: response.data.balance,
          transactions: response.data.transactions.slice(0, parseInt(configs.donation_erc20_max_orders || 20)),
          loading: false,
          error: null
        });
      } else {
        console.error('ERC20 数据获取失败:', response);
        setErc20Data(prev => ({
          ...prev,
          loading: false,
          error: '获取数据失败，请稍后刷新重试'
        }));
      }
    } catch (error: any) {
      console.error('获取 ERC20 数据异常:', error);
      setErc20Data(prev => ({
        ...prev,
        loading: false,
        error: error.message || '获取数据失败'
      }));
    }
  };

  const renderWalletCard = (
    title: string,
    subtitle: string,
    address: string,
    balance: string,
    transactions: Transaction[],
    loading: boolean,
    error: string | null,
    explorerBaseUrl: string,
    onRefresh: () => void,
    onDonate: () => void
  ) => (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
      <div className="bg-gradient-to-r from-pink-500 to-rose-600 px-6 py-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Wallet className="w-6 h-6" />
          {title}
        </h2>
        <p className="text-pink-100 text-sm mt-1">{subtitle}</p>
      </div>

      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-slate-500 text-sm">{language === 'en' ? 'Payment Address' : '收款地址'}</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="bg-slate-100 px-3 py-2 rounded-lg text-sm font-mono text-slate-700 break-all">
                {formatAddress(address)}
              </code>
            </div>
          </div>

          <div className="text-right">
            <div className="text-slate-500 text-sm mb-1">{language === 'en' ? 'USDT Balance' : 'USDT 余额'}</div>
            <div className="text-3xl font-bold text-pink-600">{balance}</div>
            <div className="text-slate-400 text-sm">USDT</div>
            <button
              onClick={onDonate}
              className="mt-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:from-pink-600 hover:to-purple-600 transition-colors font-medium text-sm shadow-md"
            >
              🧡 {language === 'en' ? 'Donate' : '捐助'}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">{language === 'en' ? 'Recent Transactions' : '最近交易记录'}</h3>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1 text-sm text-pink-600 hover:bg-pink-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {language === 'en' ? 'Refresh' : '刷新'}
          </button>
        </div>

        {loading && transactions.length === 0 ? (
          <div className="py-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-pink-600 animate-spin mb-2" />
            <p className="text-slate-500">{language === 'en' ? 'Loading transaction data...' : '正在加载交易数据...'}</p>
          </div>
        ) : error && transactions.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-red-500 mb-2">{error}</p>
            <button
              onClick={onRefresh}
              className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
            >
              {language === 'en' ? 'Retry' : '重试'}
            </button>
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-slate-500">{language === 'en' ? 'No transaction records' : '暂无交易记录'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">类型</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">金额</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600 hidden md:table-cell">对方地址</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600 hidden lg:table-cell">时间</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">TxHash</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, index) => (
                  <tr
                    key={`${tx.txhash}-${index}`}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="py-3 px-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          tx.type === 'in'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {tx.type === 'in' ? (
                          <>
                            <TrendingUp className="w-3 h-3" />
                            {language === 'en' ? 'Income' : '收入'}
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-3 h-3" />
                            {language === 'en' ? 'Expense' : '支出'}
                          </>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span
                        className={`font-semibold ${
                          tx.type === 'in' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {tx.type === 'in' ? '+' : '-'}{formatAmount(tx.value)}
                      </span>
                      <span className="text-slate-400 text-sm ml-1">USDT</span>
                    </td>
                    <td className="py-3 px-2 text-sm text-slate-600 hidden md:table-cell">
                      {tx.type === 'in' ? formatAddress(tx.from) : formatAddress(tx.to)}
                    </td>
                    <td className="py-3 px-2 text-sm text-slate-500 hidden lg:table-cell">
                      {formatTime(tx.timestamp)}
                    </td>
                    <td className="py-3 px-2">
                      <a
                        href={`${explorerBaseUrl}${tx.txhash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-pink-600 hover:text-blue-800 text-sm"
                      >
                        {formatAddress(tx.txhash)}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-pink-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{configs.donation_title}</h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {configs.donation_description}
          </p>
          <div className="mt-4 flex items-center justify-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500" />
              {t('donation.readOnlyQuery')}
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500" />
              {t('donation.noPrivateKey')}
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500" />
              {parseInt(configs.donation_refresh_interval) >= 1000 ? parseInt(configs.donation_refresh_interval) / 1000 : configs.donation_refresh_interval}{t('donation.autoRefresh')}
            </span>
          </div>
        </div>

        {/* 第一行：荣誉榜 */}
        <div className="mb-8">
          <HonorList />
        </div>

        {/* 第二行：实时流水 */}
        <div className="space-y-8">
          {renderWalletCard(
          t('donation.trc20Title'),
          t('donation.trc20Subtitle'),
          configs.donation_trc20_address,
          trc20Data.balance,
          trc20Data.transactions,
          trc20Data.loading,
          trc20Data.error,
          'https://tronscan.org/#/transaction/',
          fetchTrc20Data,
          () => openDonationModal('trc20')
        )}

        {renderWalletCard(
          t('donation.erc20Title'),
          t('donation.erc20Subtitle'),
          configs.donation_erc20_address,
          erc20Data.balance,
          erc20Data.transactions,
          erc20Data.loading,
          erc20Data.error,
          'https://etherscan.io/tx/',
          fetchErc20Data,
          () => openDonationModal('erc20')
        )}
        </div>

        {/* 第三行：音乐播放器 */}
        <div className="mt-8">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">{t('donation.thankYouSupport')}</h3>
            <div className="w-full">
              <iframe 
                src={`https://music.163.com/outchain/player?type=2&id=${configs.music_song_id}&auto=1&height=66`} 
                width="100%" 
                height="90" 
                frameBorder="0" 
                allow="autoplay; encrypted-media" 
                allowFullScreen
                title="平凡天使"
                referrerPolicy="no-referrer"
              ></iframe>
            </div>
            <p className="mt-4 text-sm text-slate-600 text-center">
              邓紫棋 - 平凡天使
            </p>
            <p className="mt-2 text-xs text-slate-400 text-center">
              {t('donation.clickToPlay')}
            </p>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-slate-500">
          <p>{configs.donation_thank_you}</p>
          <p className="mt-2">{t('donation.transactionDataNote')}</p>
        </div>
      </div>

      <DonationExpansionModal
        isOpen={showDonationModal}
        onClose={closeDonationModal}
        onSuccess={() => {}}
        networkType={selectedNetworkType}
      />
    </div>
  );
};

export default Donation;
